'use client';

import { QuestionIcon } from '@/_assets/icon-components';
import { branchService } from '@/_service/branch.service';
import type { IBranch } from '@/_types';
import {
  AspectRatio,
  Badge,
  Box,
  Button,
  Flex,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { EmblaOptionsType } from 'embla-carousel';
import useEmblaCarousel from 'embla-carousel-react';
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import React from 'react';
import { buildImageSrc } from '../CategoriesList/utils';
import { Link } from '@/i18n/navigation';
import { getBrandHref } from '@/_lib/brand-href';

const OPTIONS: EmblaOptionsType = {
  dragFree: true,
};

type NightFeedingData = { branches: IBranch[]; count: number };

const NightFeeding = ({
  initialData,
}: {
  initialData?: NightFeedingData | null;
}) => {
  const t = useTranslations();

  const { data, isLoading } = useQuery({
    queryKey: ['okey'],
    queryFn: () => branchService.nightFeedingBranches({ page: 1, limit: 20 }),
    initialData: initialData ?? undefined,
  });

  const [emblaRef] = useEmblaCarousel(OPTIONS, [
    WheelGesturesPlugin(),
  ]);

  if (isLoading && !data) return null;
  if (!data?.branches?.length) return null;

  return (
    <Box pt={{ base: 5, lg: 8 }} pb={{ base: 1, lg: 3 }}>
      <Text
        fontSize={{ base: '24px', lg: '2xl' }}
        px={{ base: 4, lg: 14 }}
        fontWeight={700}
        color='#000'
      >
        Накормим ночью
      </Text>

      <Box
        className='embla'
        style={
          {
            '--slide-size': '288px',
            '--slide-spacing': '16px',
          } as React.CSSProperties
        }
      >
        <Box
          className='embla__viewport'
          px={{ base: 4, lg: 14 }}
          ref={emblaRef}
        >
          <Flex className='embla__container' py={{ base: 4, lg: 5 }}>
            {data.branches.map((branch) => {
              const imageSrc = buildImageSrc(branch?.images[0]);

              return (
                <Box className='embla__slide' key={branch?.id}>
                  <VStack
                    alignItems='flex-start'
                    rounded='2xl'
                    overflow='hidden'
                    border='1px solid #F4F4F4'
                    boxShadow='0px 0px 20px rgba(0, 0, 0, 0.06)'
                    gap='0'
                    height='100%'
                  >
                    <Box position='relative'>
                      <AspectRatio gap={0} minW='270px' ratio={270 / 164} rounded='2xl' overflow='hidden'>
                        {branch?.images && imageSrc ? (
                          <Image src={imageSrc} alt={branch.name} fill />
                        ) : (
                          <Box
                            display='flex'
                            justifyContent='center'
                            alignItems='center'
                            borderRadius='12px'
                            bg='background'
                          >
                            <QuestionIcon />
                          </Box>
                        )}
                      </AspectRatio>

                      <Badge
                        position='absolute'
                        top='3'
                        left='3'
                        color='white'
                        bg='#5FBE5D'
                        fontSize='xs'
                        fontWeight='medium'
                        px='2'
                        py='1'
                        borderRadius='10px'
                      >
                        до {branch.close_time}
                      </Badge>
                    </Box>

                    <VStack
                      w='full'
                      flex='1'
                      alignItems='flex-start'
                      justifyContent='space-between'
                      bg='white'
                      px='3'
                      py='4'
                      gap='4'
                    >
                      <VStack gap='1' alignItems='flex-start'>
                        <Text
                          as='p'
                          fontSize={{ base: 'lg', lg: 'xl' }}
                          fontWeight='bold'
                          color='primary'
                          lineHeight='100%'
                        >
                          {branch.shipper_name}
                        </Text>
                        <Box
                          fontSize={{ base: 'md', lg: 'lg' }}
                          fontWeight={400}
                          color='dark.cardTitle'
                          lineClamp={2}
                        >
                          {branch.name}
                        </Box>
                      </VStack>

                      <Button
                        width='full'
                        variant='subtle'
                        color='#1e1e1e'
                        borderRadius='12px'
                        size='xl'
                        h='48px'
                        bg='background'
                        asChild
                      >
                        <Link
                          href={getBrandHref(branch.shipper_id, {
                            path: '/reserve',
                            query: { branch_id: branch.slug || branch.id },
                          })}
                        >
                          {t('book')}
                        </Link>
                      </Button>
                    </VStack>
                  </VStack>
                </Box>
              );
            })}
          </Flex>
        </Box>
      </Box>
    </Box>
  );
};

export default NightFeeding;
